// src/pages/BesoinDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { besoinsAPI, positionnementAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Edit2, Save, X, MapPin, Building2, User,
  Calendar, Briefcase, RefreshCw, Clock,
} from 'lucide-react';
import TracabilityInfo from '../components/common/TracabilityInfo';

const ETATS = [
  'Nouveau','En cour de traitement','En avance de phase',
  'En avance de phase via apporteur ou contact chaud',
  'Gagné','Perdu','Abandonné','reporté','cloturé','rejeté',
];

const ETAT_COLORS = {
  'Nouveau':'#38BDF8','En cour de traitement':'#FF4100','Gagné':'#22C55E',
  'Perdu':'#EF4444','Abandonné':'#F97316','reporté':'#FCD34D',
  'cloturé':'#6B7280','rejeté':'#DC2626',
};

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:12 }}>
      <span style={{ color:'var(--text-muted)', flexShrink:0, marginTop:2 }}>{icon}</span>
      <div>
        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:1, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</div>
        <div style={{ fontSize:'0.875rem', color:'var(--text-primary)' }}>{value}</div>
      </div>
    </div>
  );
}

export default function BesoinDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [besoin,   setBesoin]   = useState(null);
  const [positionements, setPositionnements] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bRes, pRes] = await Promise.all([
        besoinsAPI.get(id),
        positionnementAPI.list({ besoin_id: id, limit: 100 }).catch(() => ({ data: { items: [] } })),
      ]);
      setBesoin(bRes.data);
      setPositionnements(pRes.data?.items || []);
    } catch { toast.error('Besoin introuvable'); navigate('/besoins'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [id]);

  const startEdit = () => {
    setForm({
      titre:               besoin.titre || '',
      etat:                besoin.etat || '',
      societe_nom:         besoin.societe_nom || '',
      lieu:                besoin.lieu || '',
      type_besoin:         besoin.type_besoin || '',
      date_demarrage:      besoin.date_demarrage || '',
      date_fin:            besoin.date_fin || '',
      date_reponse:        besoin.date_reponse || '',
      duree:               besoin.duree || '',
      contact_nom:         besoin.contact_nom || '',
      contact_prenom:      besoin.contact_prenom || '',
      responsable_manager: besoin.responsable_manager || '',
      responsable_rh:      besoin.responsable_rh || '',
      budget_envisage:     besoin.budget_envisage || '',
      description:         besoin.description || '',
      criteres_requis:     besoin.criteres_requis || '',
      etape_motif:         besoin.etape_motif || '',
      motif_commentaire:   besoin.motif_commentaire || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await besoinsAPI.patch(id, form);
      setBesoin(res.data);
      setEditing(false);
      toast.success('Besoin mis à jour');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'48px', gap:10, color:'var(--text-muted)' }}>
      <div className="spinner"/> Chargement…
    </div>
  );
  if (!besoin) return null;

  const etatColor = ETAT_COLORS[besoin.etat] || '#6B7280';

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/besoins')}><ArrowLeft size={18}/></button>
          <div>
            <h1 style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              {besoin.titre}
              <span style={{ padding:'3px 12px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:etatColor+'22', color:etatColor }}>
                {besoin.etat}
              </span>
            </h1>
            <p style={{ color:'var(--text-muted)', fontSize:'0.85rem' }}>
              {besoin.reference} · {besoin.societe_nom}
            </p>
          </div>
        </div>
        <div className="page-header-actions">
          {!editing ? (
            <button className="btn btn-secondary" onClick={startEdit}><Edit2 size={15}/> Modifier</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setEditing(false)} disabled={saving}><X size={15}/> Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <div className="spinner" style={{ width:13, height:13 }}/> : <Save size={15}/>} Enregistrer
              </button>
            </>
          )}
        </div>
      </div>

      {/* Traçabilité */}
      <div style={{ marginBottom: 16 }}>
        <TracabilityInfo
          createdAt={besoin.created_at || besoin.date_creation}
          createdById={besoin.created_by_user_id}
          updatedAt={besoin.updated_at}
          updatedById={besoin.updated_by_user_id}
          showWarningIfNoData
        />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:20 }}>

        {/* Colonne gauche */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Infos principales */}
          <div className="card">
            <div className="card-header"><span className="card-title">Informations</span></div>
            {editing ? (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Titre</label><input style={inp} value={form.titre} onChange={e => setForm(f => ({...f, titre:e.target.value}))}/></div>
                <div><label style={lbl}>État</label>
                  <select style={inp} value={form.etat} onChange={e => setForm(f => ({...f, etat:e.target.value}))}>
                    {ETATS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Société</label><input style={inp} value={form.societe_nom} onChange={e => setForm(f => ({...f, societe_nom:e.target.value}))}/></div>
                <div><label style={lbl}>Lieu</label><input style={inp} value={form.lieu} onChange={e => setForm(f => ({...f, lieu:e.target.value}))}/></div>
                <div><label style={lbl}>Type</label><input style={inp} value={form.type_besoin} onChange={e => setForm(f => ({...f, type_besoin:e.target.value}))}/></div>
                <div><label style={lbl}>Démarrage</label><input style={inp} value={form.date_demarrage} onChange={e => setForm(f => ({...f, date_demarrage:e.target.value}))}/></div>
                <div><label style={lbl}>Date de fin</label><input style={inp} value={form.date_fin} onChange={e => setForm(f => ({...f, date_fin:e.target.value}))}/></div>
                <div><label style={lbl}>Date de réponse</label><input style={inp} value={form.date_reponse} onChange={e => setForm(f => ({...f, date_reponse:e.target.value}))}/></div>
                <div><label style={lbl}>Durée</label><input style={inp} value={form.duree} onChange={e => setForm(f => ({...f, duree:e.target.value}))}/></div>
                <div><label style={lbl}>Contact</label><input style={inp} value={form.contact_nom} onChange={e => setForm(f => ({...f, contact_nom:e.target.value}))} placeholder="Nom"/></div>
                <div><label style={lbl}>Resp. manager</label><input style={inp} value={form.responsable_manager} onChange={e => setForm(f => ({...f, responsable_manager:e.target.value}))}/></div>
                <div><label style={lbl}>Resp. RH</label><input style={inp} value={form.responsable_rh} onChange={e => setForm(f => ({...f, responsable_rh:e.target.value}))}/></div>
                <div><label style={lbl}>Budget envisagé</label><input style={inp} value={form.budget_envisage} onChange={e => setForm(f => ({...f, budget_envisage:e.target.value}))}/></div>
                <div><label style={lbl}>Motif étape</label><input style={inp} value={form.etape_motif} onChange={e => setForm(f => ({...f, etape_motif:e.target.value}))}/></div>
                <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Description</label>
                  <textarea rows={4} style={{ ...inp, resize:'vertical' }} value={form.description} onChange={e => setForm(f => ({...f, description:e.target.value}))}/>
                </div>
                <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Critères requis</label>
                  <textarea rows={4} style={{ ...inp, resize:'vertical' }} value={form.criteres_requis} onChange={e => setForm(f => ({...f, criteres_requis:e.target.value}))}/>
                </div>
                <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Commentaire motif</label>
                  <textarea rows={2} style={{ ...inp, resize:'vertical' }} value={form.motif_commentaire} onChange={e => setForm(f => ({...f, motif_commentaire:e.target.value}))}/>
                </div>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 24px' }}>
                <InfoRow icon={<Building2 size={14}/>} label="Société"        value={besoin.societe_nom}/>
                <InfoRow icon={<MapPin size={14}/>}    label="Lieu"           value={besoin.lieu}/>
                <InfoRow icon={<Briefcase size={14}/>} label="Type"           value={besoin.type_besoin}/>
                <InfoRow icon={<User size={14}/>}      label="Contact"        value={[besoin.contact_prenom, besoin.contact_nom].filter(Boolean).join(' ')}/>
                <InfoRow icon={<Calendar size={14}/>}  label="Démarrage"      value={besoin.date_demarrage}/>
                <InfoRow icon={<Calendar size={14}/>}  label="Date de fin"    value={besoin.date_fin}/>
                <InfoRow icon={<Calendar size={14}/>}  label="Date réponse"   value={besoin.date_reponse}/>
                <InfoRow icon={<Clock size={14}/>}     label="Durée"          value={besoin.duree}/>
                <InfoRow icon={<User size={14}/>}      label="Resp. manager"  value={besoin.responsable_manager}/>
                <InfoRow icon={<User size={14}/>}      label="Resp. RH"       value={besoin.responsable_rh}/>
                <InfoRow icon={<Briefcase size={14}/>} label="Budget envisagé" value={besoin.budget_envisage}/>
                <InfoRow icon={<Briefcase size={14}/>} label="Pôle / Agence"  value={[besoin.pole, besoin.agence].filter(Boolean).join(' / ')}/>
                {besoin.domaine_intervention && <div style={{ gridColumn:'1/-1' }}><InfoRow icon={<Briefcase size={14}/>} label="Domaine" value={besoin.domaine_intervention}/></div>}
              </div>
            )}
          </div>

          {/* Description */}
          {!editing && besoin.description && (
            <div className="card">
              <div className="card-header"><span className="card-title">Description</span></div>
              <div style={{ fontSize:'0.875rem', lineHeight:1.8, whiteSpace:'pre-wrap', color:'var(--text-secondary)' }}>{besoin.description}</div>
            </div>
          )}

          {/* Critères */}
          {!editing && besoin.criteres_requis && (
            <div className="card">
              <div className="card-header"><span className="card-title">Critères requis</span></div>
              <div style={{ fontSize:'0.875rem', lineHeight:1.8, whiteSpace:'pre-wrap', color:'var(--text-secondary)' }}>{besoin.criteres_requis}</div>
            </div>
          )}

          {/* Motif */}
          {!editing && (besoin.etape_motif || besoin.motif_commentaire) && (
            <div className="card">
              <div className="card-header"><span className="card-title">Motif / Commentaire</span></div>
              {besoin.etape_motif && <div style={{ fontWeight:600, fontSize:'0.875rem', marginBottom:6, color:'#F59E0B' }}>{besoin.etape_motif}</div>}
              {besoin.motif_commentaire && <div style={{ fontSize:'0.875rem', lineHeight:1.7 }}>{besoin.motif_commentaire}</div>}
            </div>
          )}

          {/* Positionnements liés */}
          {positionements.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Positionnements liés ({positionements.length})</span></div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Réf.</th><th>Candidat / Ressource</th><th>État</th><th>TJM</th><th>Date</th></tr></thead>
                  <tbody>
                    {positionements.map(p => {
                      const ec = ETAT_COLORS[p.etat] || '#6B7280';
                      return (
                        <tr key={p.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/positionnements/${p.id}`)}>
                          <td style={{ fontFamily:'monospace', fontSize:'0.78rem', color:'var(--text-muted)' }}>{p.reference}</td>
                          <td style={{ fontWeight:600 }}>{[p.cand_prenom, p.cand_nom].filter(Boolean).join(' ')}</td>
                          <td><span style={{ padding:'2px 8px', borderRadius:20, fontSize:'0.72rem', fontWeight:700, background:ec+'22', color:ec }}>{p.etat}</span></td>
                          <td style={{ fontWeight:600, color:'#EA580C' }}>{p.tarif_journalier_ht || '—'}</td>
                          <td style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{p.date_creation ? new Date(p.date_creation).toLocaleDateString('fr-FR') : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Colonne droite */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Traçabilité</span></div>
            <InfoRow icon={<Calendar size={14}/>} label="Date de création (Boond)" value={besoin.date_creation}/>
            <InfoRow icon={<Calendar size={14}/>} label="Créé dans l'app le" value={besoin.created_at ? new Date(besoin.created_at).toLocaleDateString('fr-FR', {day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : null}/>
            <InfoRow icon={<RefreshCw size={14}/>} label="Dernière MAJ" value={besoin.updated_at ? new Date(besoin.updated_at).toLocaleDateString('fr-FR', {day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : null}/>
            {besoin.updated_by_name && <InfoRow icon={<User size={14}/>} label="Modifié par" value={besoin.updated_by_name}/>}
            <InfoRow icon={<Briefcase size={14}/>} label="Source" value={besoin.source === 'AO' ? 'AO traité' : 'CRM / Boond'}/>
            <InfoRow icon={<Briefcase size={14}/>} label="Référence" value={besoin.reference}/>
          </div>
        </div>

      </div>
    </div>
  );
}

const lbl = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' };
const inp = { width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
